import { Modal } from "components/Modal/Modal";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { closeModal } from "store/modals/slice";
import styles from "./CountriesModal.module.scss";
import Button from "components/buttons/Button";
import {
  clearDomainCountries,
  getAvailableMirrorDomains,
  getDomainCountries,
  getCountriesList,
  handleClear,
  handleSelectDomain,
  updateCountries,
} from "store/countries/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import SearchDropdown, {
  Item,
} from "components/dropdowns/SearchDropdown/SearchDropdown";
import DefaultSelect from "components/dropdowns/DefaultSelect/DefaultSelect";
import Loader from "components/global/Loader";
import Tooltip from "components/Tooltip";
import { countriesLimits } from "configs/limits";

const CountriesModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const countriesList = useAppSelector((store) => store.settings.countries);
  const type = useAppSelector((store) => store.country.selectedType);
  const domainCountryCodes = useAppSelector(
    (store) => store.country.domainCountryCodes,
  );
  const availableDomains = useAppSelector(
    (store) => store.country.availableDomains,
  );
  const isDomainCountriesFetching = useAppSelector(
    (store) => store.country.isDomainCountriesFetching,
  );
  const isDomainsFetching = useAppSelector(
    (store) => store.country.isDomainsFetching,
  );
  const [selectedDomain, setSelectedDomain] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCountries, setSelectedCountries] = useState<Item[]>([]);

  const countryItems: Item[] = useMemo(
    () =>
      countriesList?.map((country) => ({
        value: country.code,
        label: country.name,
        icon: `https://flagpedia.net/data/flags/icon/16x12/${country.code.toLowerCase()}.png`,
      })) ?? [],
    [countriesList],
  );

  const domainOptions = useMemo(
    () => availableDomains.map((domain) => ({ value: domain, label: domain })),
    [availableDomains],
  );

  useEffect(() => {
    dispatch(getAvailableMirrorDomains())
      .unwrap()
      .catch(() => {
        dispatch(
          showNotification({
            title: t("error"),
            message: t("entityFetchError"),
            type: NotificationTypes.error,
          }),
        );
      });
  }, [dispatch, t]);

  useEffect(() => {
    const defaults = domainCountryCodes.map((code) => ({
      value: code,
      label:
        countryItems.find((country) => country.value === code)?.label ?? code,
      icon:
        countryItems.find((country) => country.value === code)?.icon ??
        `https://flagpedia.net/data/flags/icon/16x12/${code.toLowerCase()}.png`,
    }));

    setSelectedCountries(defaults);
  }, [domainCountryCodes, countryItems]);

  const loadDomainCountries = async (domain: string) => {
    try {
      await dispatch(getDomainCountries({ domain, type })).unwrap();
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityFetchError"),
          type: NotificationTypes.error,
        }),
      );
    }
  };

  const handleDomainSelect = (value: string | number) => {
    const domain = String(value);
    setSelectedDomain(domain);
    setSelectedCountries([]);
    dispatch(clearDomainCountries());
    void loadDomainCountries(domain);
  };

  const handleCloseClick = () => {
    navigate(location.pathname);
    dispatch(closeModal());
    dispatch(handleClear());
  };

  const handleSelect = (item: Item & { removed?: boolean }) => {
    setSelectedCountries((previous) =>
      item.removed
        ? previous.filter((country) => country.value !== item.value)
        : previous.some((country) => country.value === item.value)
          ? previous
          : [...previous, item],
    );
  };

  const handleApply = async () => {
    if (!selectedDomain || isDomainCountriesFetching) return;

    const selectedCodes = selectedCountries.map(({ value }) => String(value));

    setIsSaving(true);
    try {
      await dispatch(
        updateCountries({
          domain: selectedDomain,
          type,
          countries: selectedCodes,
        }),
      ).unwrap();
      await dispatch(
        getDomainCountries({ domain: selectedDomain, type }),
      ).unwrap();
      dispatch(handleSelectDomain(selectedDomain));
      await dispatch(
        getCountriesList({
          domain: selectedDomain,
          type,
          limit: countriesLimits[1],
          offset: 0,
        }),
      ).unwrap();

      navigate(location.pathname);
      dispatch(closeModal());
      dispatch(
        showNotification({
          title: t("success"),
          message: t("entityUpdateSuccess"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      await loadDomainCountries(selectedDomain);
      dispatch(
        showNotification({
          title: t("error"),
          message: t("entityUpdateError"),
          type: NotificationTypes.error,
        }),
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      className={styles["countries-modal"]}
      onClose={handleCloseClick}
      size="sm"
    >
      <Modal.Header>
        {type === "white"
          ? t("whitePremiumCountries")
          : t("restrictedCountries")}
      </Modal.Header>
      <Modal.Content>
        <div className={styles.contentWrapper}>
          <section className={styles.step}>
            <div className={styles.stepTitle}>
              <span className={styles.stepNumber}>1</span>
              <span>{t("selectDomain")}</span>
              <Tooltip text={t("geoDomainTooltip")} position="right">
                <button type="button" className="info-icon">
                  ℹ
                </button>
              </Tooltip>
            </div>
            <div className={styles.domainSelect}>
              <DefaultSelect
                value={selectedDomain}
                placeholder={t("selectDomain")}
                options={domainOptions}
                onSelect={handleDomainSelect}
                disabled={isDomainsFetching || isSaving}
              />
            </div>
          </section>

          <section className={styles.step}>
            <div className={styles.stepTitle}>
              <span className={styles.stepNumber}>2</span>
              <span>{t("selectCountries")}</span>
              <Tooltip
                text={
                  type === "white"
                    ? t("whiteCountriesTooltip")
                    : t("restrictedCountriesTooltip")
                }
                position="right"
              >
                <button type="button" className="info-icon">
                  ℹ
                </button>
              </Tooltip>
            </div>
            <div className={styles.countryField}>
              <SearchDropdown
                label={t("selectCountry")}
                items={countryItems}
                defaultValues={selectedCountries}
                onSelect={handleSelect}
                visibleItems={10}
                disabled={
                  !selectedDomain || isDomainCountriesFetching || isSaving
                }
              />
              {isDomainCountriesFetching && (
                <div className={styles.countryLoader}>
                  <Loader size={6} />
                </div>
              )}
            </div>
          </section>
        </div>
      </Modal.Content>
      <Modal.Footer className={styles["countries-footer"]}>
        <Button isSecondary txt={t("cancel")} handleClick={handleCloseClick} />
        <Button
          isSecondary
          isLoad={isSaving}
          disabled={!selectedDomain || isDomainCountriesFetching || isSaving}
          handleClick={handleApply}
          txt={t("save")}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default CountriesModal;
